import { useRef, type ReactNode } from 'react';

import { Button } from '@gearbelt/ui/components';

import CloseIcon from '../icons/close';
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle } from './dialog';

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmVariant?: 'default' | 'success' | 'destructive';
  confirmIcon?: ReactNode;
  onConfirm: () => void;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmVariant = 'default',
  confirmIcon,
  onConfirm,
}: ConfirmDialogProps) {
  // A consumer typically clears the state that feeds `title`/`description` the instant it closes the dialog,
  // which would unmount this content while Radix is still playing the 200ms close animation — the card loses
  // height and visibly shifts as it fades. Retain the last non-empty values (written during render, so they're
  // correct on the closing frame before paint) and render those, so the content stays stable through the close.
  const retainedTitle = useRef(title);
  const retainedDescription = useRef(description);
  if (title) retainedTitle.current = title;
  if (description) retainedDescription.current = description;
  const shownTitle = title || retainedTitle.current;
  const shownDescription = description || retainedDescription.current;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex flex-col gap-2.5">
        <DialogClose className="focus:ring-default-blue data-[state=open]:bg-default-blue/10 data-[state=open]:text-default-text ml-auto size-4 cursor-pointer rounded-sm opacity-70 ring-offset-white transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-none disabled:pointer-events-none">
          <CloseIcon className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogClose>

        <DialogTitle hideCloseButton className="justify-center text-center">
          {shownTitle}
        </DialogTitle>

        {shownDescription ? <DialogDescription className="text-center">{shownDescription}</DialogDescription> : null}

        <div className="mt-4 flex justify-center gap-2">
          <Button
            type="button"
            variant={confirmVariant}
            icon={confirmIcon}
            onClick={() => {
              onConfirm();
              onOpenChange(false);
            }}>
            {confirmLabel}
          </Button>
          <Button type="button" variant="grey" onClick={() => onOpenChange(false)}>
            {cancelLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
